const GRID_SIZE = 4;
const clamp01 = (value) => Math.max(0, Math.min(1, value));
const round = (value, digits = 4) => Number(value.toFixed(digits));
function isScreenPixel(type, r, g, b) {
    if (type === 'green')
        return g >= 40 && g - Math.max(r, b) >= 20 && g >= r * 1.25 && g >= b * 1.1;
    if (type === 'blue')
        return b >= 40 && b - Math.max(r, g) >= 20 && b >= r * 1.25 && b >= g * 1.1;
    return false;
}
function detectScreenType(data) {
    let green = 0;
    let blue = 0;
    const pixels = data.length / 4;
    for (let offset = 0; offset < data.length; offset += 4) {
        const r = data[offset] ?? 0;
        const g = data[offset + 1] ?? 0;
        const b = data[offset + 2] ?? 0;
        if (isScreenPixel('green', r, g, b))
            green++;
        if (isScreenPixel('blue', r, g, b))
            blue++;
    }
    const dominant = Math.max(green, blue);
    if (dominant / Math.max(1, pixels) < 0.12)
        return 'unknown';
    return green > blue ? 'green' : blue > green ? 'blue' : 'unknown';
}
function classifyShape(component, mask, width) {
    const boxWidth = component.maxX - component.minX + 1;
    const boxHeight = component.maxY - component.minY + 1;
    const aspect = boxWidth / Math.max(1, boxHeight);
    const fillRatio = component.area / (boxWidth * boxHeight);
    const centerX = Math.round(component.sumX / component.area);
    const centerY = Math.round(component.sumY / component.area);
    let horizontal = 0;
    let vertical = 0;
    for (let x = component.minX; x <= component.maxX; x++)
        horizontal += mask[centerY * width + x] ?? 0;
    for (let y = component.minY; y <= component.maxY; y++)
        vertical += mask[y * width + centerX] ?? 0;
    const crossScore = Math.min(horizontal / boxWidth, vertical / boxHeight) * clamp01((0.68 - fillRatio) / 0.35);
    let perimeter = 0;
    for (const index of component.pixels) {
        const x = index % width;
        const y = Math.floor(index / width);
        if (x === component.minX || x === component.maxX || y === component.minY || y === component.maxY ||
            !mask[index - 1] || !mask[index + 1] || !mask[index - width] || !mask[index + width])
            perimeter++;
    }
    const circularity = clamp01((4 * Math.PI * component.area) / Math.max(1, perimeter * perimeter));
    const roundAspect = clamp01(1 - Math.abs(1 - aspect));
    if (crossScore >= 0.48 && roundAspect > 0.55)
        return { type: 'cross', shapeConfidence: clamp01(crossScore) };
    if (circularity >= 0.48 && roundAspect > 0.58 && fillRatio > 0.45) {
        return { type: 'circle', shapeConfidence: clamp01(circularity * 0.7 + roundAspect * 0.3) };
    }
    if ((aspect >= 1.45 || aspect <= 0.69) && fillRatio >= 0.45) {
        return { type: 'tape', shapeConfidence: clamp01(Math.abs(Math.log(aspect)) / 1.5 + fillRatio * 0.35) };
    }
    return { type: 'unknown', shapeConfidence: clamp01(fillRatio * 0.5 + roundAspect * 0.25) };
}
/** Fast connected-component marker detection constrained to an apparent chroma screen. */
export class TrackingMarkerAnalyzer {
    id = 'tracking-markers';
    label = 'Tracking Markers';
    analyze(frame, context) {
        const startedAt = performance.now();
        const { width, height, data } = frame;
        const screenResult = context.analyzerResults.get('screen-quality');
        const screenMetrics = screenResult?.metrics;
        const screenType = screenMetrics?.screen_type ?? detectScreenType(data);
        const totalPixels = width * height;
        const mask = new Uint8Array(totalPixels);
        const contrastValues = new Float32Array(totalPixels);
        let screenR = 0;
        let screenG = 0;
        let screenB = 0;
        let screenPixels = 0;
        for (let index = 0; index < totalPixels; index++) {
            const offset = index * 4;
            const r = data[offset] ?? 0;
            const g = data[offset + 1] ?? 0;
            const b = data[offset + 2] ?? 0;
            if (isScreenPixel(screenType, r, g, b)) {
                screenR += r;
                screenG += g;
                screenB += b;
                screenPixels++;
            }
        }
        const meanR = screenPixels ? screenR / screenPixels : 0;
        const meanG = screenPixels ? screenG / screenPixels : 0;
        const meanB = screenPixels ? screenB / screenPixels : 0;
        if (screenType !== 'unknown') {
            for (let index = 0; index < totalPixels; index++) {
                const offset = index * 4;
                const r = data[offset] ?? 0;
                const g = data[offset + 1] ?? 0;
                const b = data[offset + 2] ?? 0;
                const distance = Math.sqrt((r - meanR) ** 2 + (g - meanG) ** 2 + (b - meanB) ** 2);
                const lumaDelta = Math.abs((0.2126 * r + 0.7152 * g + 0.0722 * b) -
                    (0.2126 * meanR + 0.7152 * meanG + 0.0722 * meanB));
                contrastValues[index] = Math.max(distance / 220, lumaDelta / 150);
                if (!isScreenPixel(screenType, r, g, b) && distance >= 72 && lumaDelta >= 35)
                    mask[index] = 1;
            }
        }
        const visited = new Uint8Array(totalPixels);
        const components = [];
        const neighbors = [[-1, 0], [1, 0], [0, -1], [0, 1], [-1, -1], [1, -1], [-1, 1], [1, 1]];
        for (let start = 0; start < totalPixels; start++) {
            if (!mask[start] || visited[start])
                continue;
            const queue = [start];
            visited[start] = 1;
            const component = {
                pixels: [], area: 0, minX: width, minY: height, maxX: 0, maxY: 0, sumX: 0, sumY: 0, contrastSum: 0,
            };
            for (let cursor = 0; cursor < queue.length; cursor++) {
                const index = queue[cursor];
                const x = index % width;
                const y = Math.floor(index / width);
                component.pixels.push(index);
                component.area++;
                component.minX = Math.min(component.minX, x);
                component.maxX = Math.max(component.maxX, x);
                component.minY = Math.min(component.minY, y);
                component.maxY = Math.max(component.maxY, y);
                component.sumX += x;
                component.sumY += y;
                component.contrastSum += contrastValues[index] ?? 0;
                for (const [dx, dy] of neighbors) {
                    const nx = x + dx;
                    const ny = y + dy;
                    if (nx < 0 || nx >= width || ny < 0 || ny >= height)
                        continue;
                    const next = ny * width + nx;
                    if (mask[next] && !visited[next]) {
                        visited[next] = 1;
                        queue.push(next);
                    }
                }
            }
            components.push(component);
        }
        const minArea = Math.max(7, Math.round(totalPixels * 0.00035));
        const maxArea = Math.max(minArea + 1, totalPixels * 0.025);
        const maxDimension = Math.min(width, height) * 0.16;
        const subjectComponents = components.filter((component) => component.area > maxArea);
        const markerCandidates = components.filter((component) => {
            const boxWidth = component.maxX - component.minX + 1;
            const boxHeight = component.maxY - component.minY + 1;
            return component.area >= minArea && component.area <= maxArea &&
                boxWidth >= 3 && boxHeight >= 3 && boxWidth <= maxDimension && boxHeight <= maxDimension;
        });
        const markers = markerCandidates
            .map((component, index) => {
            const x = component.sumX / component.area;
            const y = component.sumY / component.area;
            const shape = classifyShape(component, mask, width);
            const contrastConfidence = clamp01(component.contrastSum / component.area);
            const sizeConfidence = clamp01(component.area / Math.max(12, minArea * 2));
            return {
                id: `marker_${String(index + 1).padStart(3, '0')}`,
                type: shape.type,
                x: round(x, 2),
                y: round(y, 2),
                normalized_x: round(x / width),
                normalized_y: round(y / height),
                radius: round(Math.sqrt(component.area / Math.PI), 2),
                confidence: round(clamp01(contrastConfidence * 0.45 + shape.shapeConfidence * 0.4 + sizeConfidence * 0.15)),
            };
        })
            .filter((marker) => marker.confidence >= 0.42)
            .slice(0, 64)
            .map((marker, index) => ({ ...marker, id: `marker_${String(index + 1).padStart(3, '0')}` }));
        const occupied = new Set(markers.map((marker) => {
            const column = Math.min(GRID_SIZE - 1, Math.floor(marker.normalized_x * GRID_SIZE));
            const row = Math.min(GRID_SIZE - 1, Math.floor(marker.normalized_y * GRID_SIZE));
            return row * GRID_SIZE + column;
        }));
        const xs = markers.map((marker) => marker.normalized_x);
        const ys = markers.map((marker) => marker.normalized_y);
        const spreadX = markers.length ? Math.max(...xs) - Math.min(...xs) : 0;
        const spreadY = markers.length ? Math.max(...ys) - Math.min(...ys) : 0;
        let nearestSum = 0;
        for (let index = 0; index < markers.length; index++) {
            let nearest = 1;
            for (let other = 0; other < markers.length; other++) {
                if (index === other)
                    continue;
                nearest = Math.min(nearest, Math.hypot(markers[index].normalized_x - markers[other].normalized_x, markers[index].normalized_y - markers[other].normalized_y));
            }
            nearestSum += nearest;
        }
        const meanNearest = markers.length > 1 ? nearestSum / markers.length : 0;
        const distributionScore = markers.length === 0 ? 0 : clamp01(Math.min(1, markers.length / 8) * 0.25 +
            (occupied.size / Math.min(GRID_SIZE * GRID_SIZE, Math.max(8, markers.length))) * 0.35 +
            Math.min(1, spreadX / 0.65) * 0.15 +
            Math.min(1, spreadY / 0.65) * 0.15 +
            Math.min(1, meanNearest / 0.18) * 0.1);
        const clustered = markers.length >= 3 &&
            ((spreadX * spreadY < 0.08) || occupied.size <= Math.max(1, Math.floor(markers.length / 3)) || meanNearest < 0.07);
        let distributionStatus = 'unknown';
        if (markers.length > 0 && markers.length < 4)
            distributionStatus = 'sparse';
        else if (clustered)
            distributionStatus = 'clustered';
        else if (markers.length >= 6 && distributionScore >= 0.62)
            distributionStatus = 'good';
        else if (markers.length >= 4)
            distributionStatus = 'uneven';
        const nearSubject = subjectComponents.length > 0 && markers.some((marker) => subjectComponents.some((subject) => {
            const dx = Math.max(subject.minX - marker.x, 0, marker.x - subject.maxX);
            const dy = Math.max(subject.minY - marker.y, 0, marker.y - subject.maxY);
            return Math.hypot(dx, dy) < Math.min(width, height) * 0.06;
        }));
        const warnings = [];
        const warning = (code, message) => {
            warnings.push({ code, severity: 'warning', message, module_id: this.id, frame_number: context.frameNumber });
        };
        if (markers.length === 0)
            warning('no_reliable_tracking_markers', 'No reliable tracking markers detected');
        else {
            if (markers.length < 4)
                warning('too_few_tracking_markers', 'Too few tracking markers detected');
            if (distributionStatus === 'clustered')
                warning('tracking_markers_clustered', 'Markers are clustered');
            if (occupied.size <= 5)
                warning('large_marker_free_area', 'Large screen area has no markers');
            if (nearSubject)
                warning('markers_near_subject', 'Markers may be too close to subject');
            if (distributionStatus === 'uneven')
                warning('markers_uneven', 'Markers are not evenly distributed');
        }
        const overlays = [];
        for (let index = 1; index < GRID_SIZE; index++) {
            overlays.push({
                type: 'line',
                points: [{ x: index / GRID_SIZE, y: 0 }, { x: index / GRID_SIZE, y: 1 }],
                label: 'Marker distribution grid',
                color: '#6ee7ff',
                opacity: 0.28,
                stroke_width: 1,
            }, {
                type: 'line',
                points: [{ x: 0, y: index / GRID_SIZE }, { x: 1, y: index / GRID_SIZE }],
                label: 'Marker distribution grid',
                color: '#6ee7ff',
                opacity: 0.28,
                stroke_width: 1,
            });
        }
        for (let cell = 0; cell < GRID_SIZE * GRID_SIZE; cell++) {
            if (occupied.has(cell) || markers.length === 0)
                continue;
            const column = cell % GRID_SIZE;
            const row = Math.floor(cell / GRID_SIZE);
            overlays.push({
                type: 'rectangle',
                points: [
                    { x: column / GRID_SIZE, y: row / GRID_SIZE },
                    { x: (column + 1) / GRID_SIZE, y: (row + 1) / GRID_SIZE },
                ],
                label: 'Empty marker zone',
                color: '#ff9f43',
                opacity: 0.18,
                stroke_width: 1,
            });
        }
        markers.forEach((marker) => {
            overlays.push({
                type: 'circle',
                points: [{ x: marker.normalized_x, y: marker.normalized_y }],
                radius: marker.radius / Math.min(width, height),
                label: marker.id,
                color: '#ffe66d',
                opacity: 0.95,
                stroke_width: 2,
            }, {
                type: 'text',
                points: [{ x: marker.normalized_x, y: marker.normalized_y }],
                label: `${marker.id} · ${Math.round(marker.confidence * 100)}%`,
                color: '#ffffff',
                opacity: 1,
            });
        });
        const confidence = screenType === 'unknown'
            ? 0.15
            : clamp01((screenMetrics?.coverage_percentage ?? (screenPixels / Math.max(1, totalPixels)) * 100) / 100 * 0.35 +
                (markers.length ? markers.reduce((sum, marker) => sum + marker.confidence, 0) / markers.length : 0.35) * 0.65);
        return {
            module_id: this.id,
            module_name: this.label,
            confidence: round(confidence),
            frame_number: context.frameNumber,
            timestamp_ms: context.timestampMs,
            metrics: {
                marker_count: markers.length,
                markers,
                distribution_score: round(distributionScore),
                distribution_status: distributionStatus,
                screen_coverage_with_markers: round((occupied.size / (GRID_SIZE * GRID_SIZE)) * 100, 2),
                marker_candidate_count_before_filtering: markerCandidates.length,
                marker_count_after_filtering: markers.length,
                used_screen_quality_result: screenResult !== undefined,
                screen_type_used: screenType,
                processing_time_ms: round(performance.now() - startedAt, 3),
            },
            warnings,
            overlays,
        };
    }
}
//# sourceMappingURL=TrackingMarkerAnalyzer.js.map